import { Injectable } from '@angular/core';
import { DynamicFirebase } from '../classes/dynamic-firebase';
import { Companies } from '../resources/companies';
import { AuthService } from './authentication.service';
import { FormMessages } from '../messages/form-messages.enum';
import { Fmt } from '../messages/fmt';
import { NgxSpinnerService } from 'ngx-spinner';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { Observable } from 'rxjs';
import { FirebaseApi } from '../classes/firebase-api';

export interface FirestoreSubcollectionRealtimeOptions {
  collection: string;
  documentId: string;
  subcollection?: string;
  companyId?: string;
  queryBuilder?: (query: firebase.firestore.Query) => firebase.firestore.Query;
}

@Injectable({
  providedIn: 'root'
})
export class DynamicFirebaseService {

  constructor(
    private authService: AuthService,
    public spinner: NgxSpinnerService
  ) { }
  async initializeFirebaseAppByNit(companyId: string): Promise<boolean> {
    return DynamicFirebase.configFirebaseByCompanyId(companyId);
  }

  getStorage(companyId: string) {
    return DynamicFirebase.getStorageByCompanyId(companyId);
  }

  getFirestore(companyId: string) {
    return DynamicFirebase.getFirestoreByCompanyId(companyId);
  }

  async deleteAllFirebaseApps(): Promise<void> {
    await DynamicFirebase.deleteAllApps();
  }

  async deleteAllFirebaseInstances() {
    this.spinner.show();
    try {
      await this.deleteAllFirebaseApps();
    } catch (error) {
      this.spinner.hide();
      console.error(Fmt.string(FormMessages.FIREBASE_INSTANCES_ERROR, 'eliminando'), error);
    } finally {
      this.spinner.hide();
    }
  }

  async initializeAllFirebaseApps(): Promise<void> {
    let filteredCompanyIds = this.getCompanyIds();
    const initializationPromises = filteredCompanyIds.map(companyId => this.initializeFirebaseAppByNit(companyId));
    this.spinner.show();
    try {
      await Promise.all(initializationPromises);
    } catch (error) {
      this.spinner.hide();
      console.error(Fmt.string(FormMessages.FIREBASE_INSTANCES_ERROR, 'creando'), error);
    } finally {
      this.spinner.hide();
    }
  }

  public getCompanyIds(): Array<string> {
    const companiesNit = Companies.companiesNIT;
    const companyIds = Object.values(companiesNit);
    return companyIds.filter(companyId => companyId !== this.authService.getUserSession().clientCompanyId);
  }

  /**
   * @description Creates a realtime listener for a Firestore subcollection and returns its unsubscribe function.
   */
  public async watchSubcollection<T = any>(
    options: FirestoreSubcollectionRealtimeOptions,
    onChange: (documents: Array<T & { id: string }>) => void,
    onError?: (error: any) => void
  ): Promise<() => void> {
    if (!options || !options.collection || !options.documentId) {
      throw new Error('collection and documentId are required');
    }

    if (!options.subcollection && options.queryBuilder) {
      throw new Error('queryBuilder solo aplica cuando se envía subcollection');
    }

    let firestore: firebase.firestore.Firestore;
    if (options.companyId) {
      const initialized = await DynamicFirebase.configFirebaseByCompanyId(options.companyId);
      if (!initialized) {
        throw new Error(`No se pudo inicializar Firebase para companyId: ${options.companyId}`);
      }
      firestore = DynamicFirebase.getFirestoreByCompanyId(options.companyId);
    } else {
      firestore = FirebaseApi.firestore;
    }

    const documentRef = firestore
      .collection(options.collection)
      .doc(options.documentId);

    if (!options.subcollection) {
      return documentRef.onSnapshot(
        (snapshot) => {
          if (!snapshot.exists) {
            if (onChange) onChange([]);
            return;
          }
          const document = {
            id: snapshot.id,
            ...(snapshot.data() as T)
          } as T & { id: string };
          if (onChange) onChange([document]);
        },
        (error) => {
          if (onError) onError(error);
          else console.error('Error escuchando documento en tiempo real', error);
        }
      );
    }

    const subcollectionRef = documentRef.collection(options.subcollection);

    const query = options.queryBuilder ? options.queryBuilder(subcollectionRef) : subcollectionRef;
    const unsubscribe = query.onSnapshot(
      (snapshot) => {
        const documents = snapshot.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...(docSnapshot.data() as T)
        })) as Array<T & { id: string }>;
        if (onChange) onChange(documents);
      },
      (error) => {
        if (onError) onError(error);
        else console.error('Error escuchando subcolección en tiempo real', error);
      }
    );

    return unsubscribe;
  }

  /**
   * @description Returns an observable that emits document/subcollection data on each realtime change.
   */
  public watchSubcollection$<T = any>(options: FirestoreSubcollectionRealtimeOptions): Observable<Array<T & { id: string }>> {
    return new Observable<Array<T & { id: string }>>((subscriber) => {
      let unsubscribe: () => void = () => { };

      this.watchSubcollection<T>(
        options,
        (documents) => subscriber.next(documents),
        (error) => subscriber.error(error)
      )
        .then((unsubscribeCallback) => unsubscribe = unsubscribeCallback)
        .catch((error) => subscriber.error(error));

      return () => unsubscribe();
    });
  }
}
